/**
 * ClarosPeopleHome.jsx — AOS-UX-KPI-02A
 * People & Workforce Dashboard
 *
 * Authoritative data sources:
 *   - Workforce stats    : GET /api/v1/peo01/stats           (canonical PostgreSQL, PEO-01)
 *   - Dept headcount     : GET /api/v1/peo01/department-summary
 *   - Approval queue     : GET /api/v1/peo03/approvals/stats, /pending  (PEO-03)
 *   - Exception counts   : GET /api/v1/peo03/exceptions/summary
 *   - Personal faculty   : GET /api/v1/people/*              (legacy MongoDB — labeled)
 *
 * Permission model (mirrors peo_rbac.py — backend enforces; frontend gates display only):
 *   canManagePeople  → people.worker.view (institution_admin, hr_admin, dean, registrar, department_head)
 *   canApproveLeave  → people.leave.approve (institution_admin, hr_admin, dean, department_head)
 *   canViewIdentity  → people.identity.view (institution_admin, hr_admin)
 *
 * Governance rules:
 *   - No fabricated metrics, trends or percentages.
 *   - Compensation data is never exposed here.
 *   - SYNTHETIC_DEMO badge always present on the hero.
 *   - No duplicate App Shell.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Users2, Award, BookOpen, Trophy, Loader2, Plus, Sparkles, X,
  Building2, Clock, AlertTriangle, UserCheck, ChevronRight,
  CalendarCheck, BarChart3, ShieldAlert, FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LegacyDataBanner } from "../components/shared/LegacyDataBanner";
import {
  PremiumHeroBanner,
  MetricCard,
  MetricCardGrid,
  DashboardPanel,
  QuickActionGrid,
} from "../components/shared/DashboardMetrics";

// ── Role constants (mirrors peo_rbac.py) ─────────────────────────────────────
const WORKFORCE_ROLES  = ["super_admin", "institution_admin", "hr_admin", "dean", "department_head", "registrar"];
const APPROVE_ROLES    = ["super_admin", "institution_admin", "hr_admin", "dean", "department_head"];
const IDENTITY_ROLES   = ["super_admin", "institution_admin", "hr_admin"];
const FACULTY_ADMIN    = ["super_admin", "institution_admin", "hod", "dean", "hr_admin"];

// ── Sub-components (personal/legacy view) ────────────────────────────────────
function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card p-4 border border-border" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="text-2xl font-semibold tabular-nums leading-tight mt-1">{value ?? "—"}</div>
    </div>
  );
}

function ApiGauge({ score, maxScore = 140 }) {
  const safeScore = Number.isFinite(score) ? score : 0;
  const pct = Math.min(100, (safeScore / maxScore) * 100);
  return (
    <div className="space-y-2" data-testid="api-gauge">
      <div className="text-3xl font-semibold tabular-nums">
        {safeScore.toFixed(0)}
        <span className="text-sm font-normal text-muted-foreground">/{maxScore}</span>
      </div>
      <div className="h-3 w-full bg-muted rounded overflow-hidden">
        <div
          className={`h-full transition-all ${pct >= 70 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-muted-foreground">{pct.toFixed(0)}% of maximum</div>
    </div>
  );
}

function LogTrainingDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    training_type: "FDP", title: "", organiser: "", duration_days: 1,
    completion_date: new Date().toISOString().slice(0, 10),
    certificate_url: "", platform: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post("/v1/people/training", { ...form, duration_days: Number(form.duration_days) });
      toast.success("Logged");
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="people-log-training-btn">
          <Plus className="h-4 w-4 mr-2" /> Log Training
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="people-training-dialog">
        <DialogHeader><DialogTitle>Log Training Record</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select
            data-testid="training-type"
            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={form.training_type}
            onChange={(e) => setForm(s => ({ ...s, training_type: e.target.value }))}
          >
            {["FDP", "STTP", "WORKSHOP", "ONLINE_COURSE", "CONFERENCE"].map(t =>
              <option key={t} value={t}>{t}</option>
            )}
          </select>
          <Input data-testid="training-title" placeholder="Title" value={form.title}
            onChange={(e) => setForm(s => ({ ...s, title: e.target.value }))} />
          <Input data-testid="training-org" placeholder="Organiser" value={form.organiser}
            onChange={(e) => setForm(s => ({ ...s, organiser: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="training-days" type="number" placeholder="Days"
              value={form.duration_days}
              onChange={(e) => setForm(s => ({ ...s, duration_days: e.target.value }))} />
            <Input data-testid="training-date" type="date" value={form.completion_date}
              onChange={(e) => setForm(s => ({ ...s, completion_date: e.target.value }))} />
          </div>
          <Input data-testid="training-cert" placeholder="Certificate URL (optional)"
            value={form.certificate_url}
            onChange={(e) => setForm(s => ({ ...s, certificate_url: e.target.value }))} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="training-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ClarosPeopleHome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const canManagePeople = WORKFORCE_ROLES.includes(user?.role);
  const canApproveLeave = APPROVE_ROLES.includes(user?.role);
  const canViewIdentity = IDENTITY_ROLES.includes(user?.role);
  const isAdmin = FACULTY_ADMIN.includes(user?.role);

  const [tab, setTab] = useState("DASH");

  // ── Workforce dashboard state (canonical APIs) ──────────────────────────
  const [workerStats, setWorkerStats]     = useState(null);
  const [approvalStats, setApprovalStats] = useState(null);
  const [exceptions, setExceptions]       = useState(null);
  const [deptSummary, setDeptSummary]     = useState([]);
  const [pendingLeave, setPendingLeave]   = useState([]);
  const [dashLoading, setDashLoading]     = useState(false);

  // ── Personal faculty state (legacy MongoDB APIs) ────────────────────────
  const [me, setMe]             = useState(null);
  const [apiScore, setApiScore] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [training, setTraining] = useState([]);
  const [plan, setPlan]         = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [allFaculty, setAllFaculty]   = useState([]);

  // ── Data loaders ────────────────────────────────────────────────────────
  const loadWorkforceDash = useCallback(async () => {
    if (!canManagePeople) return;
    setDashLoading(true);
    try {
      const base = [
        api.get("/v1/peo01/stats").then(r => r.data).catch(() => null),
        api.get("/v1/peo01/department-summary").then(r => r.data).catch(() => []),
        api.get("/v1/peo03/exceptions/summary").then(r => r.data).catch(() => null),
      ];
      if (canApproveLeave) {
        base.push(
          api.get("/v1/peo03/approvals/stats").then(r => r.data).catch(() => null),
          api.get("/v1/peo03/approvals/pending", { params: { limit: 5 } }).then(r => r.data).catch(() => []),
        );
      }
      const [statsR, deptR, excR, appStatsR, pendR] = await Promise.all(base);
      if (statsR)          setWorkerStats(statsR);
      if (deptR)           setDeptSummary(deptR);
      if (excR)            setExceptions(excR);
      if (appStatsR)       setApprovalStats(appStatsR);
      if (Array.isArray(pendR)) setPendingLeave(pendR);
    } catch {
      toast.error("Failed to load workforce data");
    } finally {
      setDashLoading(false);
    }
  }, [canManagePeople, canApproveLeave]);

  const loadPersonal = useCallback(async () => {
    try {
      const promises = [
        api.get("/v1/people/faculty/me").then(r => r.data).catch(() => null),
        api.get("/v1/people/workload/me").then(r => r.data).catch(() => null),
        api.get("/v1/people/training").then(r => r.data).catch(() => []),
      ];
      if (isAdmin) {
        promises.push(api.get("/v1/people/faculty").then(r => r.data).catch(() => []));
      }
      const [meR, wlR, trR, facR] = await Promise.all(promises);
      setMe(meR);
      setWorkload(wlR);
      setTraining(trR || []);
      if (facR) setAllFaculty(facR);
      if (meR) {
        const [aR, pR] = await Promise.all([
          api.get(`/v1/people/faculty/${meR.id}/api`).then(r => r.data).catch(() => null),
          api.get(`/v1/people/faculty/${meR.id}/development-plan`).then(r => r.data).catch(() => null),
        ]);
        setApiScore(aR);
        setPlan(pR);
      }
    } catch {
      toast.error("Failed to load faculty data");
    }
  }, [isAdmin]);

  useEffect(() => {
    loadWorkforceDash();
    loadPersonal();
  }, [loadWorkforceDash, loadPersonal]);

  // ── Action handlers ─────────────────────────────────────────────────────
  const genPlan = async () => {
    if (!me) return toast.error("Faculty record required");
    setPlanLoading(true);
    try {
      const r = await api.post(`/v1/people/faculty/${me.id}/development-plan`, {});
      setPlan(r.data);
      toast.success("Plan generated");
    } catch {
      toast.error("Generation failed");
    } finally {
      setPlanLoading(false);
    }
  };

  const computeApi = async (fid) => {
    try {
      const r = await api.post(`/v1/people/faculty/${fid}/api/compute`);
      toast.success(`API recomputed: ${r.data.total_api}/140`);
      if (me && fid === me.id) setApiScore(r.data);
      loadPersonal();
    } catch {
      toast.error("Compute failed");
    }
  };

  const deleteTraining = async (tid) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/v1/people/training/${tid}`);
      toast.success("Deleted");
      loadPersonal();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────
  const totalExceptions = exceptions
    ? (exceptions.pending_leave_approvals || 0) +
      (exceptions.attendance_exceptions || 0) +
      (exceptions.missing_identity_links || 0) +
      (exceptions.overlapping_leave || 0)
    : null;

  const quickActions = [
    {
      label: "Worker Directory",
      icon: <Users2 className="w-4 h-4 text-blue-600" />,
      iconBg: "bg-blue-50",
      onClick: () => navigate("/people/employment"),
      testid: "qa-worker-directory",
    },
    ...(canApproveLeave ? [{
      label: "Leave Approvals",
      icon: <CalendarCheck className="w-4 h-4 text-amber-600" />,
      iconBg: "bg-amber-50",
      onClick: () => navigate("/people/approvals"),
      testid: "qa-leave-approvals",
    }] : []),
    {
      label: "Attendance & Leave",
      icon: <Clock className="w-4 h-4 text-emerald-600" />,
      iconBg: "bg-emerald-50",
      onClick: () => navigate("/people/attendance-leave"),
      testid: "qa-attendance-leave",
    },
    ...(canViewIdentity ? [{
      label: "Identity Management",
      icon: <UserCheck className="w-4 h-4 text-purple-600" />,
      iconBg: "bg-purple-50",
      onClick: () => navigate("/people/identity"),
      testid: "qa-identity-mgmt",
    }] : []),
  ];

  const tabs = [
    { id: "DASH",  label: canManagePeople ? "People Dashboard" : "My Dashboard" },
    { id: "TRAIN", label: "Training" },
    { id: "PLAN",  label: "Development Plan" },
    ...(isAdmin ? [{ id: "ADMIN", label: "People Admin" }] : []),
  ];

  const hasApproveOrIdentity = canApproveLeave || canViewIdentity;

  return (
    <div className="space-y-6" data-testid="people-page">

      {/* ── Tab bar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 border-b border-border">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`people-tab-${t.id.toLowerCase()}`}
            className={`px-3 py-2 -mb-px border-b-2 text-sm transition-colors ${
              tab === t.id
                ? "border-[#1765d1] text-[#1765d1] font-medium"
                : "border-transparent text-muted-foreground hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          PEOPLE DASHBOARD TAB — workforce view (canManagePeople)
          ════════════════════════════════════════════════════════════════════ */}
      {tab === "DASH" && canManagePeople && (
        <div className="space-y-5" data-testid="people-workforce-dash">

          {/* Premium hero */}
          <PremiumHeroBanner
            title="People & Workforce"
            badgeLabel="HR Dashboard"
            badgeVariant="blue"
            subtitle="Vaagdevi College of Engineering · Workforce Management"
            demoNotice="[DEMO] Synthetic workforce data — illustrative only; canonical PEO-01 records"
            icon={<Users2 className="w-7 h-7 text-blue-400" />}
          >
            <div className="flex flex-col items-end gap-2">
              <div className="text-right" data-testid="hero-active-count">
                <p className="text-4xl font-bold text-white tabular-nums leading-none">
                  {workerStats ? workerStats.active_workers : (dashLoading ? "…" : "—")}
                </p>
                <p className="text-sm text-slate-300 mt-0.5">Active Employees</p>
              </div>
              <Button
                data-testid="btn-view-directory"
                onClick={() => navigate("/people/employment")}
                className="bg-blue-600 hover:bg-blue-500 text-white shadow-md"
                size="sm"
              >
                <Users2 className="w-4 h-4 mr-2" /> View Directory
              </Button>
            </div>
          </PremiumHeroBanner>

          {/* KPI cards — always rendered for canManagePeople */}
          <MetricCardGrid data-testid="people-kpi-grid">
            <MetricCard
              data-testid="kpi-active-workforce"
              variant="blue"
              label="Active Workforce"
              value={workerStats ? workerStats.active_workers : "…"}
              sub={workerStats ? `${workerStats.total_workers} total workers on record` : "loading"}
              icon={<Users2 className="w-5 h-5 text-white" />}
            />
            <MetricCard
              data-testid="kpi-departments"
              variant="green"
              label="Departments"
              value={workerStats ? workerStats.departments : "…"}
              sub={workerStats
                ? `${workerStats.teaching_faculty} teaching · ${workerStats.non_teaching_staff} non-teaching`
                : "loading"}
              icon={<Building2 className="w-5 h-5 text-white" />}
            />
            <MetricCard
              data-testid="kpi-pending-approvals"
              variant="amber"
              label="Pending Approvals"
              value={canApproveLeave
                ? (approvalStats ? approvalStats.pending_count : "…")
                : "—"}
              sub={canApproveLeave && approvalStats
                ? `avg ${approvalStats.avg_pending_days}d waiting`
                : canApproveLeave ? "loading" : "leave.approve required"}
              icon={<Clock className="w-5 h-5 text-white" />}
            />
            <MetricCard
              data-testid="kpi-exceptions"
              variant="purple"
              label="Open Exceptions"
              value={totalExceptions !== null ? totalExceptions : "…"}
              sub="leave · attendance · identity"
              icon={<AlertTriangle className="w-5 h-5 text-white" />}
            />
          </MetricCardGrid>

          {/* Operational panels */}
          <div
            className={`grid grid-cols-1 ${hasApproveOrIdentity ? "lg:grid-cols-2" : ""} gap-5`}
          >
            {/* ── Left column: approval queue + exceptions ── */}
            {hasApproveOrIdentity && (
              <div className="space-y-5">

                {/* Pending Leave Approvals */}
                {canApproveLeave && (
                  <DashboardPanel
                    data-testid="panel-pending-leave"
                    title="Pending Leave Approvals"
                    icon={<CalendarCheck className="w-4 h-4 text-amber-500" />}
                    action={
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => navigate("/people/approvals")}
                        data-testid="view-all-approvals-btn"
                      >
                        View All <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    }
                  >
                    {pendingLeave.length === 0 ? (
                      <div
                        className="flex items-center justify-center py-6 text-slate-400 text-sm gap-2"
                        data-testid="no-pending-leave"
                      >
                        <CalendarCheck className="w-4 h-4 opacity-40" />
                        No pending requests
                      </div>
                    ) : (
                      <div className="space-y-2" data-testid="pending-leave-list">
                        {pendingLeave.map(item => (
                          <div
                            key={item.request_id}
                            data-testid={`leave-item-${item.request_id}`}
                            className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-100"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {item.employee_name}
                              </p>
                              <p className="text-xs text-slate-400">
                                {item.leave_type_name} · {item.requested_days} day(s)
                              </p>
                            </div>
                            <Badge
                              className={`ml-3 shrink-0 text-xs border ${
                                item.days_pending > 5
                                  ? "bg-red-50 text-red-600 border-red-200"
                                  : "bg-amber-50 text-amber-600 border-amber-200"
                              }`}
                            >
                              {item.days_pending}d pending
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </DashboardPanel>
                )}

                {/* Exception Breakdown */}
                {(canApproveLeave || canViewIdentity) && exceptions && (
                  <DashboardPanel
                    data-testid="panel-exceptions"
                    title="Exception Breakdown"
                    icon={<ShieldAlert className="w-4 h-4 text-purple-500" />}
                  >
                    <div className="space-y-2.5">
                      {canApproveLeave && (
                        <>
                          <ExcRow
                            dot="bg-amber-400"
                            label="Pending Leave Approvals"
                            value={exceptions.pending_leave_approvals}
                            textCls="text-amber-600"
                            testid="exc-pending-leave"
                          />
                          <ExcRow
                            dot="bg-red-400"
                            label="Overlapping Leave"
                            value={exceptions.overlapping_leave}
                            textCls="text-red-600"
                            testid="exc-overlapping"
                          />
                          <ExcRow
                            dot="bg-orange-400"
                            label="Attendance Exceptions (90 days)"
                            value={exceptions.attendance_exceptions}
                            textCls="text-orange-600"
                            testid="exc-attendance"
                          />
                        </>
                      )}
                      {canViewIdentity && (
                        <ExcRow
                          dot="bg-purple-400"
                          label="Missing Identity Links"
                          value={exceptions.missing_identity_links}
                          textCls="text-purple-600"
                          testid="exc-identity"
                        />
                      )}
                    </div>
                  </DashboardPanel>
                )}
              </div>
            )}

            {/* ── Right column: quick actions + dept overview ── */}
            <div className="space-y-5">
              <DashboardPanel
                data-testid="panel-quick-actions"
                title="Quick Actions"
                icon={<FileText className="w-4 h-4 text-blue-500" />}
              >
                <QuickActionGrid actions={quickActions} />
              </DashboardPanel>

              {deptSummary.length > 0 && (
                <DashboardPanel
                  data-testid="panel-dept-summary"
                  title="Workforce by Department"
                  icon={<BarChart3 className="w-4 h-4 text-emerald-500" />}
                >
                  <div className="space-y-2">
                    {deptSummary.slice(0, 7).map(dept => (
                      <div
                        key={dept.id}
                        data-testid={`dept-row-${dept.id}`}
                        className="flex items-center justify-between py-0.5"
                      >
                        <span className="text-sm text-slate-600 truncate flex-1">{dept.name}</span>
                        <span className="text-sm font-semibold text-slate-800 tabular-nums ml-3">
                          {dept.headcount}
                        </span>
                      </div>
                    ))}
                  </div>
                </DashboardPanel>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          PEOPLE DASHBOARD TAB — personal view (faculty / non-manager)
          ════════════════════════════════════════════════════════════════════ */}
      {tab === "DASH" && !canManagePeople && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="people-dash-pane">
          <LegacyDataBanner module="Faculty & People" variant="inline" />
          <div className="card p-5 border border-border lg:col-span-1">
            <div className="label-eyebrow mb-1">Academic Performance Index</div>
            {me ? (
              <>
                <ApiGauge score={apiScore?.total_api} />
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Teaching</div>
                    <div className="font-semibold tabular-nums">{apiScore?.teaching_score ?? 0}/50</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Research</div>
                    <div className="font-semibold tabular-nums">{apiScore?.research_score ?? 0}/60</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Service</div>
                    <div className="font-semibold tabular-nums">{apiScore?.service_score ?? 0}/30</div>
                  </div>
                </div>
                <Button
                  size="sm" variant="outline" className="mt-3"
                  onClick={() => computeApi(me.id)}
                  data-testid="recompute-api-btn"
                >
                  Recompute
                </Button>
              </>
            ) : (
              <div className="text-sm text-muted-foreground" data-testid="api-gauge-na">
                No personal API score — your role does not have an associated faculty profile.
                Use the <span className="font-medium">People Admin</span> tab to compute scores.
              </div>
            )}
          </div>
          <div className="card p-5 border border-border">
            <div className="label-eyebrow mb-2">Current workload</div>
            <Stat testid="workload-courses" icon={BookOpen} label="Courses this semester"
              value={workload?.courses_count} />
            <div className="mt-2 text-xs text-muted-foreground">
              {workload?.teaching_hours_week ?? 0} hours/week · {workload?.students_count ?? 0} students
            </div>
          </div>
          <div className="card p-5 border border-border">
            <div className="label-eyebrow mb-2">Training summary</div>
            <Stat testid="training-count" icon={Award} label="Records" value={training.length} />
            <div className="mt-2 text-xs text-muted-foreground">
              Last: {training[0]?.title || "—"}
            </div>
          </div>
          {plan && (
            <div className="card p-5 border border-border lg:col-span-3" data-testid="latest-plan">
              <div className="label-eyebrow mb-2">Latest development plan goals</div>
              <ul className="list-disc ml-5 text-sm space-y-1">
                {(plan.goals?.goals || []).map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TRAINING TAB
          ════════════════════════════════════════════════════════════════════ */}
      {tab === "TRAIN" && (
        <div className="space-y-3" data-testid="people-train-pane">
          <LegacyDataBanner module="Training Records" variant="inline" />
          <div className="flex items-center justify-end">
            <LogTrainingDialog onCreated={loadPersonal} />
          </div>
          <div className="card border border-border divide-y">
            {training.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground" data-testid="training-empty">
                No training records yet.
              </div>
            ) : training.map(t => (
              <div
                key={t.id}
                className="p-3 flex items-start justify-between gap-3"
                data-testid={`training-row-${t.id}`}
              >
                <div>
                  <div className="text-sm font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {t.organiser} · {t.duration_days} day(s)
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{t.training_type}</Badge>
                  <span className="text-xs text-muted-foreground">{t.completion_date}</span>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => deleteTraining(t.id)}
                    data-testid={`training-delete-${t.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          DEVELOPMENT PLAN TAB
          ════════════════════════════════════════════════════════════════════ */}
      {tab === "PLAN" && (
        <div className="space-y-3" data-testid="people-plan-pane">
          <LegacyDataBanner module="AI Development Plans" variant="inline" />
          <div className="card p-5 border border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold">AI Development Plan</h3>
              <Button onClick={genPlan} disabled={planLoading} data-testid="generate-plan-btn">
                {planLoading
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Sparkles className="h-4 w-4 mr-2" />}
                Generate My Plan
              </Button>
            </div>
            {!plan && (
              <div className="text-sm text-muted-foreground">
                No plan yet — click Generate.
              </div>
            )}
            {plan && plan.goals && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="plan-output">
                <div className="border rounded p-3">
                  <div className="label-eyebrow mb-2">Goals</div>
                  <ul className="list-disc ml-5 text-sm space-y-1">
                    {(plan.goals.goals || []).map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
                <div className="border rounded p-3">
                  <div className="label-eyebrow mb-2">Recommended courses</div>
                  <ul className="list-disc ml-5 text-sm space-y-1">
                    {(plan.goals.courses || []).map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
                <div className="border rounded p-3">
                  <div className="label-eyebrow mb-2">Research direction</div>
                  <p className="text-sm">{plan.goals.research_tip}</p>
                </div>
                <div className="border rounded p-3">
                  <div className="label-eyebrow mb-2">Service activity</div>
                  <p className="text-sm">{plan.goals.service}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          PEOPLE ADMIN TAB (admins only — legacy faculty directory)
          ════════════════════════════════════════════════════════════════════ */}
      {tab === "ADMIN" && isAdmin && (
        <div className="space-y-3" data-testid="people-admin-pane">
          <LegacyDataBanner module="Faculty Directory" variant="inline" />
          <div className="card border border-border divide-y">
            {allFaculty.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                No faculty records found.
              </div>
            ) : allFaculty.map(f => (
              <div
                key={f.id}
                className="p-3 flex items-center justify-between gap-3 text-sm"
                data-testid={`faculty-row-${f.id}`}
              >
                <div>
                  <div className="font-medium">{f.display_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {f.designation || "Faculty"} · {f.qualification || "—"}
                  </div>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => computeApi(f.id)}
                  data-testid={`faculty-compute-${f.id}`}
                >
                  Compute API
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ExcRow helper (local to this file) ─────────────────────────────────────
function ExcRow({ dot, label, value, textCls, testid }) {
  return (
    <div className="flex items-center justify-between py-0.5" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      <span className={`text-sm font-semibold tabular-nums ${textCls}`}>{value}</span>
    </div>
  );
}
